import { computed, SetupContext } from '@vue/composition-api';

import { mixpanel } from '@/helpers/analytics';

import { useUsersWithSWRV } from '@/modules/advisor-search/api/useUsersWithSWRV';

import { useDocumentDescription } from '@/modules/advisor-search/utils/useDocumentDescription';
import { useDocumentTitle } from '@/modules/advisor-search/utils/useDocumentTitle';

const toString = (x: unknown): string => {
  return typeof x === 'string' ? x : '';
};

const toPageNumber = (x: unknown): number => {
  const page = Number(x);
  if (!Number.isInteger(page) || page < 1) {
    return 1;
  }
  return page;
};

export const useAdvisorSearchUsersPage = (
  { root }: SetupContext,
  isSp: boolean,
) => {
  const query = computed(() => toString(root.$route.query.keyword));
  const page = computed(() => toPageNumber(root.$route.query.page));
  const tracker = (query: string, page: number, count: number) => {
    mixpanel.track('Topic Search Result', {
      is_sp: isSp,
      page_number: page,
      query,
      count,
    });
  };

  const { data, error, isValidating, isCached } = useUsersWithSWRV(
    query,
    page,
    tracker,
  );

  useDocumentDescription(() => query.value);

  useDocumentTitle(() => {
    if (query.value === '') {
      return 'エキスパートを検索';
    }

    return page.value > 1
      ? `${query.value}の相談(${page.value}ページ目)`
      : `${query.value}の相談`;
  });

  const heading = computed(() => {
    if (query.value === '') {
      return 'エキスパートを検索';
    }
    if (!data.value || data.value.query !== query.value) {
      return query.value;
    }

    const totalCount =
      page.value > 1
        ? `${data.value.meta.totalCount}件中 ${page.value}ページ目`
        : `${data.value.meta.totalCount}件`;

    return `${query.value}（${totalCount}）`;
  });

  const showPagination = computed(
    () =>
      data.value && data.value.users.length && data.value.query === query.value,
  );

  const onClickLink = (
    eventName:
      | 'Click Public Issue Guide Button'
      | 'Click Full Support Guide Button',
  ) => {
    mixpanel.track(eventName, {
      page: location.href,
    });
  };

  return {
    query,
    page,
    data,
    error,
    isValidating,
    isCached,
    heading,
    showPagination,
    onClickLink,
  };
};
